import React, { useMemo, useRef } from "react";
import Modal from "react-modal";
import moment from "moment";
import { Download, Printer, X } from "lucide-react";
import html2pdf from "html2pdf.js/dist/html2pdf.bundle.min.js";

moment.locale("es");

const safeText = (v, fallback = "---") =>
  v === undefined || v === null || `${v}`.trim() === "" ? fallback : `${v}`;

const parseDate = (d) => {
  if (!d) return null;
  if (d?.seconds) return new Date(d.seconds * 1000);
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? null : dt;
};

const formatDate = (d) => {
  const dt = parseDate(d);
  if (!dt) return "---";
  return moment(dt).format("D [de] MMMM [de] YYYY").replace(".", "");
};

const money = (n) => {
  const val = Number(n || 0);
  return val.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const modalStyle = {
  overlay: {
    backgroundColor: "rgba(15, 23, 42, 0.55)",
    zIndex: 2000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  content: {
    position: "relative",
    inset: "auto",
    width: "min(1200px, 98vw)",
    height: "min(92vh, 920px)",
    border: "none",
    borderRadius: 16,
    padding: 0,
    overflow: "hidden",
    background: "#fff",
    boxShadow: "0 25px 60px rgba(2, 6, 23, 0.35)",
    fontFamily: "Roboto, system-ui, -apple-system, Segoe UI, Arial, sans-serif",
  },
};

export default function PdfPreviewCotizacionModal({
  isOpen,
  onClose,
  cotizacion,
  tipo = "COTIZACIÓN",
  emisor, // opcional: si luego lo sacas de tu config
}) {
  const c = useMemo(() => cotizacion ?? {}, [cotizacion]);

  const contentRef = useRef(null);

  const folio =
    c.numeroCotizacionFormatted || c.numeroCotizacion || c.folio || "—";

  const items = useMemo(() => {
    const source =
      c.productosSeleccionados ||
      c.partidas ||
      c.conceptos ||
      c.items ||
      c.productos ||
      c.detalleProductos ||
      [];

    const arr = Array.isArray(source) ? source : [];

    return arr.map((p, idx) => {
      const cantidad = Number(p?.cantidad ?? p?.qty ?? 0);
      const precioUnitario = Number(
        p?.precioUnitario ?? p?.precio ?? p?.tarifa ?? 0,
      );

      // subtotal línea
      const subtotal = Number.isFinite(Number(p?.subtotal))
        ? Number(p.subtotal)
        : Number.isFinite(Number(p?.importe))
          ? Number(p.importe)
          : cantidad * precioUnitario;

      const descuentoLinea = Number(p?.descuentoImporte ?? p?.descuento ?? 0);

      return {
        no: idx + 1,
        sku: p?.sku || p?.SKU || p?.codigo || p?.clave || "---",
        descripcion: p?.descripcion || p?.nombre || p?.producto || "---",
        cantidad,
        unidad: p?.unidad || p?.um || p?.medida || "Pieza",
        precioUnitario,
        descuento: descuentoLinea,
        subtotal,
      };
    });
  }, [c]);

  const computed = useMemo(() => {
    const subtotalCalc = items.reduce(
      (acc, it) => acc + Number(it.subtotal || 0),
      0,
    );

    const subtotal = Number(c.subtotalBase ?? c.subtotal ?? subtotalCalc ?? 0);

    const descuentoTotal = Number(c.descuentoTotal ?? c.descuento ?? 0);
    const baseIVA = Number(c.baseIVA ?? Math.max(0, subtotal - descuentoTotal));

    const ivaPct = Number(c.ivaPorcentaje ?? c.ivaPct ?? 16);
    const iva = Number(c.iva ?? (baseIVA * ivaPct) / 100);

    const total = Number(c.total ?? baseIVA + iva);

    return { subtotal, descuentoTotal, baseIVA, ivaPct, iva, total };
  }, [c, items]);

  // EMISOR: puedes conectar luego a tu config de empresa
  const em = emisor || {
    nombre: "Servicios Técnicos Industriales S.A. de C.V.",
    rfc: "STI850615KL8",
    direccion:
      "Av. Insurgentes Sur 1605, Col. San José Insurgentes, Benito Juárez, CDMX 03900",
    telefono: "+52 55 5000-3000",
    correo: "ventas@sti.com.mx",
  };

  const clienteNombre = safeText(c.nombreCliente || c.cliente);
  const clienteRfc = safeText(c.clienteRfc || c.rfc);
  const clienteContacto = safeText(
    c.clienteContacto ||
      c.atencion ||
      c.contactoPersonal ||
      c.contacto ||
      c.nombreContacto,
  );

  const clienteDireccion = safeText(
    c.direccionCliente ||
      c.direccion ||
      c.direccionFiscal ||
      [
        c.domicilio || c.calle || "",
        c.numeroExt || c.noExt || c.numeroExterior
          ? `#${c.numeroExt || c.noExt || c.numeroExterior}`
          : "",
        c.numeroInt || c.noInt || c.numeroInterior
          ? `Int ${c.numeroInt || c.noInt || c.numeroInterior}`
          : "",
        c.colonia || c.col || "",
        c.ciudad || c.municipio || "",
        c.codigoPostal || c.cp ? `CP ${c.codigoPostal || c.cp}` : "",
      ]
        .filter(Boolean)
        .join(", "),
  );

  const clienteTelefono = safeText(c.clienteTelefono || c.telefono);
  const clienteCorreo = safeText(c.clienteEmail || c.correo || c.email);

  const asunto = safeText(c.referencia || c.asunto);
  const fecha = formatDate(c.fechaCotizacion || c.fechaEmision);
  const validaHasta = formatDate(c.validaHasta || c.fechaVencimiento);
  const version = safeText(c.version, "1");

  const handlePrint = () => {
    // imprime SOLO el contenido (con CSS print)
    window.print();
  };

  const handleDownload = async () => {
    const el = contentRef.current;
    if (!el) return;

    const opt = {
      margin: 10,
      filename: `${tipo}-${folio}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    await html2pdf().set(opt).from(el).save();
  };

  return (
    <Modal
      isOpen={Boolean(isOpen)}
      onRequestClose={onClose}
      style={modalStyle}
      ariaHideApp={false}
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
    >
      <div className="pdfv-root">
        {/* HEADER (tal cual Figma) */}
        <div className="pdfv-header">
          <h2 className="pdfv-title">
            Vista Previa PDF
            <span className="pdfv-badge">{tipo}</span>
          </h2>

          <div className="pdfv-actions">
            <button className="pdfv-btn" type="button" onClick={handleDownload}>
              <Download size={16} className="pdfv-ico pdfv-ico-blue" />
              <span>Descargar PDF</span>
            </button>

            <button className="pdfv-btn" type="button" onClick={handlePrint}>
              <Printer size={16} className="pdfv-ico" />
              <span>Imprimir</span>
            </button>

            <div className="pdfv-divider" />

            <button className="pdfv-close" type="button" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* BODY */}
        <div className="pdfv-body">
          <div className="pdfv-page print-content" ref={contentRef}>
            {/* TOP */}
            <div className="pdfv-top">
              <div>
                <h1 className="pdfv-h1">{tipo}</h1>

                <div className="pdfv-meta">
                  <p>
                    <span className="pdfv-meta-bold">Folio:</span>{" "}
                    {safeText(folio)}
                  </p>
                  <p>
                    <span className="pdfv-meta-bold">Fecha:</span> {fecha}
                  </p>
                  <p>
                    <span className="pdfv-meta-bold">Válida Hasta:</span>{" "}
                    {validaHasta}
                  </p>
                  <p>
                    <span className="pdfv-meta-bold">Versión:</span> {version}
                  </p>
                </div>
              </div>

              <div className="pdfv-logo">
                <span>LOGO</span>
              </div>
            </div>

            {/* EMISOR / CLIENTE */}
            <div className="pdfv-cols">
              <div>
                <h3 className="pdfv-h3">EMISOR</h3>
                <div className="pdfv-block">
                  <p className="pdfv-strong">{safeText(em.nombre)}</p>
                  <p>RFC: {safeText(em.rfc)}</p>
                  <p>{safeText(em.direccion)}</p>
                  <p>Teléfono: {safeText(em.telefono)}</p>
                  <p>Correo: {safeText(em.correo)}</p>
                </div>
              </div>

              <div>
                <h3 className="pdfv-h3">CLIENTE</h3>
                <div className="pdfv-block">
                  <p className="pdfv-strong">{clienteNombre}</p>
                  <p>RFC: {clienteRfc}</p>
                  <p>Contacto: {clienteContacto}</p>
                  <p>{clienteDireccion}</p>
                  <p>Teléfono: {clienteTelefono}</p>
                  <p>Correo: {clienteCorreo}</p>
                </div>
              </div>
            </div>

            {/* ASUNTO */}
            <div className="pdfv-asunto">
              <p>
                <span className="pdfv-meta-bold">Asunto:</span>{" "}
                <span className="pdfv-muted">{asunto}</span>
              </p>
            </div>

            {/* CONCEPTOS */}
            <div className="pdfv-section">
              <h3 className="pdfv-h3">CONCEPTOS</h3>

              <table className="pdfv-table">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>SKU</th>
                    <th>Descripción</th>
                    <th className="t-right">Cantidad</th>
                    <th>Unidad</th>
                    <th className="t-right">Precio Unitario</th>
                    <th className="t-right">Descuento</th>
                    <th className="t-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="pdfv-empty">
                        No hay conceptos registrados.
                      </td>
                    </tr>
                  ) : (
                    items.map((it) => (
                      <tr key={`${it.no}-${it.sku}`}>
                        <td>{it.no}</td>
                        <td>{it.sku}</td>
                        <td>{it.descripcion}</td>
                        <td className="t-right">{it.cantidad}</td>
                        <td>{it.unidad}</td>
                        <td className="t-right">${money(it.precioUnitario)}</td>
                        <td className="t-right">${money(it.descuento)}</td>
                        <td className="t-right">${money(it.subtotal)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* TOTALES */}
            <div className="pdfv-totals-wrap">
              <div className="pdfv-totals">
                <div className="pdfv-total-row">
                  <span className="pdfv-muted">Subtotal:</span>
                  <span>${money(computed.subtotal)}</span>
                </div>
                <div className="pdfv-total-row">
                  <span className="pdfv-muted">IVA ({computed.ivaPct}%):</span>
                  <span>${money(computed.iva)}</span>
                </div>
                <div className="pdfv-total-row pdfv-total-big">
                  <span>TOTAL:</span>
                  <span>${money(computed.total)}</span>
                </div>
              </div>
            </div>

            {/* TEXTOS */}
            <div className="pdfv-notes">
              <div>
                <p className="pdfv-meta-bold">Tiempo de Entrega:</p>
                <p className="pdfv-muted">{safeText(c.tiempoEntrega)}</p>
              </div>

              <div>
                <p className="pdfv-meta-bold">Términos y Condiciones:</p>
                <p className="pdfv-muted pdfv-pre">
                  {safeText(c.condicionesPago)}
                </p>
              </div>

              <div>
                <p className="pdfv-meta-bold">Notas:</p>
                <p className="pdfv-muted pdfv-pre">{safeText(c.notas)}</p>
              </div>
            </div>

            {/* FOOTER */}
            <div className="pdfv-footer">
              <p>Vendedor: {safeText(c.vendedorNombre || c.vendedor)}</p>
              <p className="pdfv-muted">
                Este documento es una cotización y no representa un compromiso
                de compra-venta hasta su aprobación formal.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
